/* ===============================
   CART DRAWER REWARD SYSTEM (fast path)
   ================================================================
   Your Liquid template already computes gift eligibility, the
   qualifying line key, the cart total, and the milestone amounts on
   every render. This script reads that straight off the DOM instead
   of re-fetching /cart.js and recomputing everything client-side -
   that redundant fetch, plus a 700ms artificial "settle" delay after
   every action, was the entire reason this felt slow. Removing it
   cuts 2+ round trips and ~700ms off every single cart change.

   /cart.js is still used, but only as a rare fallback - to clean up
   duplicate gift lines, which needs the actual line keys of the
   extra lines and isn't something the DOM alone can tell us.
================================================================= */

let confettiShippingPlayed = false;
let confettiDiscountPlayed = false;
let isUpdatingGift = false;

function changeLine(key, quantity) {
  return fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: key, quantity: quantity })
  });
}

function refreshDrawer() {
  document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
}

function giftWasManuallyDismissed() {
  try {
    return sessionStorage.getItem('giftDismissed') === '1';
  } catch (err) {
    return false;
  }
}

function clearGiftDismissal() {
  try {
    sessionStorage.removeItem('giftDismissed');
  } catch (err) {}
}

/* ===============================
   Reward bar pause state - set by the close (x) button on
   #cart-reward-bar in cart-drawer.liquid, cleared by the "Show
   offers" reopen pill. This is a SEPARATE flag from giftDismissed
   above (that one tracks "customer manually removed the gift with
   the x on the gift card itself" - this one tracks "customer closed
   the whole promo bar"). Only gates NEW auto-adds and confetti below
   - it deliberately does not force-remove a gift that was already
   added before the bar was closed, so closing an ad doesn't take
   something away from the customer's cart.
================================= */
function isRewardBarDismissed() {
  try {
    return sessionStorage.getItem('rewardBarDismissed') === '1';
  } catch (err) {
    return false;
  }
}

/* ===============================
   FAST PATH: read state from the DOM, act only if needed
================================= */
function syncGiftFromDom() {
  if (isUpdatingGift) return;

  const bar = document.getElementById('cart-reward-bar');
  const box = document.getElementById('free-gift-box');
  if (!bar || !box) return;

  const variantId = box.dataset.giftVariantId;
  if (!variantId) return;

  const eligible = box.dataset.giftEligible === 'true';
  const inCart = box.dataset.giftInCart === 'true';
  const key = box.dataset.giftKey;
  const lineCount = parseInt(box.dataset.giftLineCount || '0', 10);
  const quantity = parseInt(box.dataset.giftQuantity || '0', 10);

  if (!eligible) {
    clearGiftDismissal();
  }

  // Rare edge case: the gift ended up on more than one line, or with
  // quantity > 1. Only path that genuinely needs a /cart.js fetch,
  // since only that gives us the actual keys of the extra lines.
  if (lineCount > 1 || quantity > 1) {
    cleanupDuplicates(variantId);
    return;
  }

  if (eligible && !inCart && !giftWasManuallyDismissed() && !isRewardBarDismissed()) {
    isUpdatingGift = true;
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        properties: { _free_gift: 'true' }
      })
    })
    .then(function () {
      premiumBlast();
      refreshDrawer();
    })
    .catch(function (err) { console.error('Free gift add failed:', err); })
    .finally(function () { isUpdatingGift = false; });
    return;
  }

  if (!eligible && inCart && key) {
    isUpdatingGift = true;
    changeLine(key, 0)
      .then(function () { refreshDrawer(); })
      .catch(function (err) { console.error('Free gift remove failed:', err); })
      .finally(function () { isUpdatingGift = false; });
  }
}

/* ===============================
   FALLBACK: only runs when a duplicate is actually detected
================================= */
function cleanupDuplicates(giftVariantId) {
  isUpdatingGift = true;

  fetch('/cart.js')
    .then(function (res) { return res.json(); })
    .then(function (cart) {
      const giftItems = cart.items.filter(function (item) {
        return item.variant_id == giftVariantId;
      });

      if (giftItems.length === 0) {
        isUpdatingGift = false;
        return;
      }

      const primary = giftItems[0];
      const extras = giftItems.slice(1);
      const requests = extras.map(function (item) { return changeLine(item.key, 0); });
      if (primary.quantity > 1) requests.push(changeLine(primary.key, 1));

      return Promise.all(requests).then(function () { refreshDrawer(); });
    })
    .catch(function (err) { console.error('Gift cleanup failed:', err); })
    .finally(function () { isUpdatingGift = false; });
}

/* ===============================
   Milestone confetti - compares the server-rendered cart total
   against the milestone amounts, no fetch needed. Skipped entirely
   while the reward bar is closed, since the canvas it draws into is
   hidden (zero dimensions) at that point anyway.
================================= */
function checkMilestoneConfetti() {
  if (isRewardBarDismissed()) return;

  const bar = document.getElementById('cart-reward-bar');
  if (!bar) return;

  const totalCents = parseInt(bar.dataset.cartTotalCents || '0', 10);
  const m1 = (parseFloat(bar.dataset.milestone1) || 0) * 100;
  const m3 = (parseFloat(bar.dataset.milestone3) || 0) * 100;

  if (totalCents >= m1 && !confettiShippingPlayed) {
    premiumBlast();
    confettiShippingPlayed = true;
  }
  if (totalCents < m1) {
    confettiShippingPlayed = false;
  }

  if (totalCents >= m3 && !confettiDiscountPlayed) {
    premiumBlast();
    confettiDiscountPlayed = true;
  }
  if (totalCents < m3) {
    confettiDiscountPlayed = false;
  }
}

/* ===============================
   PREMIUM BLAST ANIMATION
================================= */
function premiumBlast() {

  const canvas = document.getElementById('confetti-canvas');
  if (!canvas) return;

  const ctx = canvas.getContext('2d');
  canvas.width = canvas.offsetWidth;
  canvas.height = canvas.offsetHeight;

  const particles = [];
  const particleCount = 140;
  const colors = ['#ff6b6b', '#ffd93d', '#6bcB77', '#4d96ff', '#ffffff', '#fb8264'];

  for (let i = 0; i < particleCount; i++) {
    const angle = Math.random() * Math.PI * 2;
    const velocity = Math.random() * 8 + 3;

    particles.push({
      x: canvas.width / 2,
      y: canvas.height / 2,
      radius: Math.random() * 5 + 2,
      color: colors[Math.floor(Math.random() * colors.length)],
      speedX: Math.cos(angle) * velocity,
      speedY: Math.sin(angle) * velocity,
      gravity: 0.08,
      alpha: 1
    });
  }

  let animationFrame;

  function animate() {
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    particles.forEach(function (p) {
      p.x += p.speedX;
      p.y += p.speedY;
      p.speedY += p.gravity;
      p.alpha -= 0.008;

      ctx.globalAlpha = p.alpha;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
      ctx.fillStyle = p.color;
      ctx.fill();
    });

    ctx.globalAlpha = 1;
    animationFrame = requestAnimationFrame(animate);
  }

  animate();

  setTimeout(function () {
    cancelAnimationFrame(animationFrame);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  }, 2000);
}

/* ===============================
   IMPACT AJAX OBSERVER
   Scoped to the cart drawer and lightly debounced (short - not the
   700ms this used to wait on top of everything else) purely to
   collapse rapid-fire DOM mutations into a single check.
================================= */
function runChecks() {
  syncGiftFromDom();
  checkMilestoneConfetti();
}

document.addEventListener('DOMContentLoaded', function () {

  runChecks();

  const target = document.getElementById('cart-drawer') || document.body;
  let debounceTimer = null;

  const observer = new MutationObserver(function () {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(runChecks, 120);
  });

  observer.observe(target, {
    childList: true,
    subtree: true
  });

  // Direct listener (not just the MutationObserver above) so that
  // closing/reopening the reward bar - a class toggle, not a
  // childList mutation the observer above would catch - still
  // triggers an immediate re-check. The reopen handler in
  // cart-drawer.liquid dispatches this same event after clearing the
  // pause flag, so the gift can be added right away if eligible,
  // rather than waiting for an unrelated cart change to happen to
  // trigger it.
  document.addEventListener('cart:refresh', runChecks);

});